"use client";

/**
 * Reusable Pipeline/Stage Selector for human escalation
 * Used in both Auto-Reply Settings and Integration Settings tabs
 */

import { useId } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertCircle } from "lucide-react";
import type { CrmPipeline, CrmStage } from "@/hooks/integrations/use-crm-binding";

interface HandoverPipelineSelectorProps {
  pipelines: CrmPipeline[];
  stages: CrmStage[];
  selectedPipelineId?: string;
  selectedStageId?: string;
  isLoadingPipelines?: boolean;
  isSaving?: boolean;
  showWarning?: boolean;
  /** Compact mode for embedding in smaller cards */
  compact?: boolean;
  /**
   * `row` puts pipeline and stage side by side and drops the visible labels, to
   * match the per-channel and exclusion rows they sit next to. Stacked stays the
   * default — the email settings tab has the width for it.
   */
  layout?: "stacked" | "row";
  onPipelineChange: (pipelineId: string) => void;
  onStageChange: (stageId: string) => void;
}

export function HandoverPipelineSelector({
  pipelines,
  stages,
  selectedPipelineId,
  selectedStageId,
  isLoadingPipelines,
  isSaving,
  showWarning,
  compact = false,
  layout = "stacked",
  onPipelineChange,
  onStageChange,
}: HandoverPipelineSelectorProps) {
  const pipelineSelectId = useId();
  const stageSelectId = useId();
  const activeStages = stages.filter((s) => !s.isArchived);

  if (isLoadingPipelines) {
    return (
      <div className="flex items-center gap-2 p-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading pipelines...</span>
      </div>
    );
  }

  if (pipelines.length === 0) {
    return (
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-600" />
          <span className="text-sm text-yellow-800">
            No pipelines available. Please check your CRM configuration.
          </span>
        </div>
      </div>
    );
  }

  const isRow = layout === "row";
  const labelClass = isRow
    ? "sr-only"
    : compact
      ? "text-xs font-medium"
      : "text-sm font-medium";
  const triggerClass = compact ? "h-8 text-xs" : "";

  return (
    <div className={isRow ? "space-y-3" : compact ? "space-y-3" : "space-y-4"}>
      <div className={isRow ? "grid grid-cols-2 gap-4" : "contents"}>
      {/* Pipeline Selection */}
      <div className="space-y-1.5">
        <Label htmlFor={pipelineSelectId} className={labelClass}>Pipeline</Label>
        <Select
          value={selectedPipelineId || ""}
          onValueChange={onPipelineChange}
          disabled={isSaving}
        >
          <SelectTrigger id={pipelineSelectId} className={triggerClass}>
            <SelectValue placeholder="Select pipeline" />
          </SelectTrigger>
          <SelectContent>
            {pipelines.map((pipeline) => (
              <SelectItem key={pipeline.id} value={pipeline.id}>
                {pipeline.name} <span>{pipeline.isDefault && "(Default)"}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stage Selection */}
      {(selectedPipelineId || isRow) && (
        <div className="space-y-1.5">
          <Label htmlFor={stageSelectId} className={labelClass}>Stage</Label>
          {activeStages.length === 0 ? (
            <div className={`text-gray-500 p-2 bg-gray-50 rounded ${compact ? "text-xs" : "text-sm"}`}>
              No stages found for this pipeline.
            </div>
          ) : (
            <Select
              value={selectedStageId || ""}
              onValueChange={onStageChange}
              disabled={isSaving || !selectedPipelineId}
            >
              <SelectTrigger id={stageSelectId} className={triggerClass}>
                <SelectValue placeholder="Select stage" />
              </SelectTrigger>
              <SelectContent>
                {activeStages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: stage.color || "#gray" }}
                      />
                      <span>{stage.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
      </div>

      {/* Warning */}
      {showWarning && (!selectedPipelineId || !selectedStageId) && (
        <div className={`flex items-center gap-2 text-amber-600 ${compact ? "text-xs" : "text-sm"}`}>
          <AlertCircle className={compact ? "w-3 h-3" : "w-4 h-4"} />
          <span>Select a pipeline and stage for escalation to work.</span>
        </div>
      )}
    </div>
  );
}
